import { escapeXML } from './escape.xml';
import {
	FRAGMENT_TAG,
	TEXT_TAG,
	TRUSTED_TEXT_TAG,
	type VNode,
	type VNodeAttrs,
	type VNodeExportOptions,
} from './index';

export const DEFAULT_OPTIONS: VNodeExportOptions = {
	lowerCaseTagName: false,
	indent: '  ',
	ignoreKeys: [],
	keyMap: {},
};

const convertStyle = (styleAttrs: object | string): string => {
	if (typeof styleAttrs === 'string') {
		return styleAttrs;
	}

	return Object.keys(styleAttrs)
		.map((tag) => {
			var value = (styleAttrs as { [key: string]: any })[tag];
			if (typeof value === 'number') {
				value = value + 'px';
			}
			return `${tag}: ${value}`;
		})
		.join(';');
};

export function exportAttrs(attrs: VNodeAttrs, options: VNodeExportOptions) {
	var output: string[] = [];
	Object.keys(attrs).forEach((key) => {
		if (options.ignoreKeys.indexOf(key) > -1) {
			return;
		}

		var value = attrs[key];
		if (value == null || value === '') {
			return;
		}

		if (key === 'style') {
			value = convertStyle(value);
		}

		if (options.keyMap[key]) {
			key = options.keyMap[key];
		}

		var type = typeof value;

		if (type === 'function' || type === 'object') {
			throw Error('Unknown value "' + key + '":"' + value + '"');
		}

		if (typeof value === 'string') {
			value = escapeXML(value);
		}

		output.push(`${key}="${value}"`);
	});

	return output.join(' ');
}

function mergeDefaultOptions(options: VNodeExportOptions): VNodeExportOptions {
	if (options == null) {
		return DEFAULT_OPTIONS;
	}

	const definedOptions = Object.fromEntries(Object.entries(options).filter(([, val]) => val !== undefined));
	return { ...DEFAULT_OPTIONS, ...definedOptions };
}

function renderNode(node: VNode, options: VNodeExportOptions, indentString: string): string {
	if (node == null) {
		return '';
	}

	var attrs = '';
	if (node.tag == null || node.tag === '') {
		throw new Error('Invalid VNode: ' + node);
	}

	var tag = node.tag;
	if (options.lowerCaseTagName) {
		tag = tag.toLowerCase();
	}

	if (node.attrs != null) {
		attrs = exportAttrs(node.attrs, options);
		if (attrs.length > 0) {
			attrs = ' ' + attrs;
		}
	}

	var indent = '';
	if (options.indent && indentString.length > 0) {
		indent = '\n' + indentString;
	}

	// Handle children
	var children = '';
	if (node.children && Array.isArray(node.children)) {
		children = node.children.map((child) => exportVNode(child, options, indentString + options.indent)).join('');
	}
	var shouldLineBreak = options.indent && indentString.length === 0 && children.length > 0;
	return [
		`<${tag}${attrs}>`,
		exportText(node.text),
		children,
		children.length > 0 ? indent : '',
		shouldLineBreak ? '\n' : '',
		`</${tag}>`,
	].join('');
}

function exportVNode(node: VNode, options: VNodeExportOptions, indentString: string): string {
	if (node == null) {
		return '';
	}

	var indent = '';
	if (options.indent && indentString.length > 0) {
		indent = '\n' + indentString;
	}

	switch (node.tag) {
		case TRUSTED_TEXT_TAG:
			return indent + (node.text == null ? node.children : node.text);
		case TEXT_TAG:
			return indent + escapeXML(node.text == null ? <any>node.children : node.text);
		case FRAGMENT_TAG:
			return node.children.map((child) => exportVNode(child, options, indentString)).join('');
		default:
			return indent + renderNode(node, options, indentString);
	}
}

function exportText(text: string): string {
	return text != null ? escapeXML(text) : '';
}

export function exportVNodeToString(node: VNode, options?: VNodeExportOptions): string {
	var currentOptions = mergeDefaultOptions(options);
	return exportVNode(node, currentOptions, '');
}
